import {createSelector} from 'reselect';
import {PrescriptionType} from 'features/wizardMs/reducers/prescription/prescription/prescriptionTypes';

import {getWizardMs} from 'features/wizardMs/selectors/wizard/wizard';
import {isEyePrescriptionEmpty, validatePrescription} from 'components/wizard/__utils';
import {getActiveUsageSku} from 'features/wizardMs/reducers/usages/usages';
import {FILL_IT, UPLOAD} from 'constants/wizard/prescriptionType';
import {getActiveUsage, isFashionUsage} from '../usages/usages';
import {WizardOptionType} from '@optimaxdev/wizard-ms';
import {getCustomerConfiguration} from 'features/wizardMs/reducers/customerConfiguration/customerConfiguration';
import {compose, equals, isEmpty, not, omit} from 'ramda';
import {ApplicationStoreType} from 'reducers';
import {SavedPrescriptionsType} from 'features/wizardMs/reducers/prescription/savedCustomerPrescription/savedCustomerPrescription';
import {contactLensesFields, fieldsToOmit} from './constants';

/**
 * get prescription values wizard ms
 * @param {ApplicationStoreType} store - ApplicationStoreType
 * @returns {PrescriptionType} - PrescriptionType
 */
export const getPrescriptionValues = createSelector(
    getWizardMs,
    ({prescription}): PrescriptionType => prescription.values,
);

/**
 * get prescription method
 * @param {ApplicationStoreType} store - ApplicationStoreType
 * @returns {number} - method
 */
export const getPrescriptionSelectedMethod = createSelector(
    getWizardMs,
    ({prescription}) => prescription.values.method,
);

/**
 * Get prescription field
 */
export const getPrescription = createSelector(getWizardMs, ({prescription}) => prescription);

/**
 * validated prescription method
 * if prescription fill correct it will return true without errors
 * if prescription fill wrong it will return false and errors
 */
export const getPrescriptionValidateResult = createSelector(
    getPrescription,
    getActiveUsageSku,
    ({values}, activeUsageSku) => {
        const [isPrescriptionValid, errors] = validatePrescription(activeUsageSku, values);

        return {
            isPrescriptionValid,
            errors,
        };
    },
);

/**
 * if prescription file upload wrong it will return boolean true or false
 */
export const isPrescriptionFileError = createSelector(
    getPrescriptionValues,
    ({file, method}) => method === UPLOAD && Boolean(file.error),
);

/**
 * Is the prescription from customer`s profile settings
 */
export const isSavedPrescription = createSelector(
    getPrescriptionValues,
    (prescription: PrescriptionType): boolean => Boolean(prescription.id),
);

/**
 * Is the prescription response fulfilled or rejected
 */
export const isPrescriptionLoaded = createSelector(
    getPrescriptionValues,
    (prescription: PrescriptionType): boolean =>
        ['fulfilled', 'rejected'].includes(prescription.status),
);

/**
 * Should update the prescription
 */
export const shouldUpdatePrescription = createSelector(
    getPrescription,
    ({shouldUpdate}) => shouldUpdate,
);

/**
 * Returns prism node
 */
export const getPrismNode = createSelector(getActiveUsage, selectedUsage => {
    if (!('items' in selectedUsage) || !selectedUsage.items) return {};
    const prismNode = (selectedUsage.items as WizardOptionType[]).find(
        item => item.alias === 'prism',
    );
    return prismNode || {};
});

/**
 * Determines added prism
 */
export const isPrismAdded = createSelector(
    getPrismNode,
    getCustomerConfiguration,
    (prismNode, {options}) => {
        if (isEmpty(prismNode)) return false;
        return Boolean(options[(prismNode as WizardOptionType).node]);
    },
);

export const getIsPrescriptionEmpty = createSelector(
    getPrescription,
    isFashionUsage,
    ({values}, isFashion) => {
        const isPrescriptionEmpty =
            isEyePrescriptionEmpty(values.od) && isEyePrescriptionEmpty(values.os);

        return isPrescriptionEmpty && values.method === FILL_IT && !isFashion;
    },
);

/**
 * Return saved prescription
 * @param {ApplicationStoreType} store - redux store
 * @returns {*} saved prescription
 */
export const getSavedCustomerPrescriptions = (
    store: ApplicationStoreType,
): SavedPrescriptionsType => store.wizardMs.prescription.savedCustomerPrescriptions;

/**
 * Sets CL fields to default values (bc we don't need them to be compared)
 * @param {PrescriptionType} prescription - prescription
 * @return {PrescriptionType} - prescription with default values for CL fields
 */
const clearCLFields = ({od, os, ...prescription}: PrescriptionType): PrescriptionType => ({
    ...prescription,
    od: {...od, ...contactLensesFields},
    os: {...os, ...contactLensesFields},
});

/**
 * Checks if current prescription values don't match
 * the values of the saved prescription
 */
export const getIsSavedPrescriptionChanged = createSelector(
    getPrescription,
    ({savedCustomerPrescriptions: {prescriptions}, values}) => {
        const savedPrescription = prescriptions.find(({id}) => id === values.id);

        if (!savedPrescription) return false;

        return compose<
            PrescriptionType,
            Omit<PrescriptionType, typeof fieldsToOmit[number]>,
            boolean,
            boolean
        >(
            not,
            equals(omit(fieldsToOmit, clearCLFields(savedPrescription))),
            omit(fieldsToOmit),
        )(clearCLFields(values));
    },
);
